import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, ThumbsUp, ThumbsDown, Clock, Send, ArrowLeft,
  Plus, Loader2, Lightbulb, Mic, Code, Globe, Trophy, Filter,
  ChevronUp, ChevronDown, User, Shield, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { communityAPI } from '../lib/api';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  'career-tips': Lightbulb, 'interview-stories': Mic, 'skill-sharing': Code,
  'uae-market': Globe, 'wins': Trophy, 'general': MessageCircle,
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function PostCard({ post, onClick }) {
  const Icon = CATEGORY_ICONS[post.category] || MessageCircle;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      onClick={onClick} data-testid={`post-${post.post_id}`}
      className="p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.005]"
      style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 pt-0.5 min-w-[32px]">
          <ChevronUp className="w-4 h-4" style={{ color: post.user_voted === 'up' ? '#0D9488' : 'var(--t-text-muted)' }} />
          <span className="text-xs font-bold" style={{ color: post.vote_count > 0 ? '#0D9488' : 'var(--t-text-muted)' }}>
            {post.vote_count}
          </span>
          <ChevronDown className="w-4 h-4" style={{ color: post.user_voted === 'down' ? '#FC8181' : 'var(--t-text-muted)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className="text-[9px] px-1.5 py-0" style={{ background: 'rgba(245,132,31,0.1)', color: '#0D9488', border: '1px solid rgba(245,132,31,0.2)' }}>
              <Icon className="w-2.5 h-2.5 mr-0.5" />{post.category}
            </Badge>
            <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{timeAgo(post.created_at)}</span>
          </div>
          <h3 className="text-sm font-bold mb-1 line-clamp-2" style={{ color: 'var(--t-text-heading)' }}>{post.title}</h3>
          <p className="text-xs line-clamp-2" style={{ color: 'var(--t-text-muted)' }}>{post.content}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--t-text-muted)' }}>
              <User className="w-3 h-3" /> {post.author_name}
              <Shield className="w-2.5 h-2.5 ml-0.5" /> Lv.{post.author_level}
            </span>
            <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--t-text-muted)' }}>
              <MessageCircle className="w-3 h-3" /> {post.comment_count}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function PostDetail({ post, onBack, onRefresh }) {
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [voteCount, setVoteCount] = useState(post.vote_count);
  const [userVote, setUserVote] = useState(post.user_voted);
  const Icon = CATEGORY_ICONS[post.category] || MessageCircle;

  const handleVote = async (type) => {
    try {
      const res = await communityAPI.vote(post.post_id, type);
      setVoteCount(res.data.vote_count);
      setUserVote(res.data.action === 'removed' ? null : type);
    } catch { toast.error('Vote failed'); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await communityAPI.addComment(post.post_id, comment);
      setComment('');
      onRefresh();
      toast.success('Comment posted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to comment'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="post-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm hover:text-[#0D9488] transition-colors"
        style={{ color: 'var(--t-text-muted)' }} data-testid="back-to-posts">
        <ArrowLeft className="w-4 h-4" /> Back to Posts
      </button>

      <div className="rounded-xl border p-5" style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}>
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-1">
            <button onClick={() => handleVote('up')} data-testid="upvote-btn"
              className="p-1 rounded hover:bg-[rgba(245,132,31,0.1)] transition-colors">
              <ChevronUp className="w-5 h-5" style={{ color: userVote === 'up' ? '#0D9488' : 'var(--t-text-muted)' }} />
            </button>
            <span className="text-sm font-bold" style={{ color: voteCount > 0 ? '#0D9488' : 'var(--t-text-muted)' }}>{voteCount}</span>
            <button onClick={() => handleVote('down')} data-testid="downvote-btn"
              className="p-1 rounded hover:bg-[rgba(245,101,101,0.1)] transition-colors">
              <ChevronDown className="w-5 h-5" style={{ color: userVote === 'down' ? '#FC8181' : 'var(--t-text-muted)' }} />
            </button>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className="text-[10px]" style={{ background: 'rgba(245,132,31,0.1)', color: '#0D9488' }}>
                <Icon className="w-3 h-3 mr-1" />{post.category}
              </Badge>
              <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{timeAgo(post.created_at)}</span>
            </div>
            <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>{post.title}</h2>
            <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--t-text-secondary)' }}>{post.content}</p>
            <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--t-border)' }}>
              <User className="w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--t-text-secondary)' }}>{post.author_name}</span>
              <Badge className="text-[9px]" style={{ background: 'rgba(168,85,246,0.1)', color: '#A855F6' }}>Lv.{post.author_level}</Badge>
              {post.author_badge_count > 0 && (
                <Badge className="text-[9px]" style={{ background: 'rgba(245,132,31,0.1)', color: '#0D9488' }}>{post.author_badge_count} badges</Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--t-text-muted)' }}>
          {post.comments?.length || 0} Comments
        </h3>
        <div className="space-y-2 mb-4">
          {(post.comments || []).map(c => (
            <div key={c.comment_id} className="p-3 rounded-lg" style={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)' }}
              data-testid={`comment-${c.comment_id}`}>
              <p className="text-sm" style={{ color: 'var(--t-text-secondary)' }}>{c.content}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] font-medium" style={{ color: 'var(--t-text-muted)' }}>{c.author_name}</span>
                <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>Lv.{c.author_level}</span>
                <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{timeAgo(c.created_at)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Add Comment */}
        <div className="flex gap-2" data-testid="comment-form">
          <input value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Write a comment..."
            className="flex-1 border rounded-lg px-3 py-2 text-sm focus:border-[#0D9488] focus:outline-none"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            onKeyDown={e => e.key === 'Enter' && handleComment()}
            data-testid="comment-input" />
          <Button onClick={handleComment} disabled={sending || !comment.trim()}
            className="h-9 px-4 rounded-lg"
            style={{ background: '#0D9488', color: '#fff' }}
            data-testid="submit-comment-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreatePostModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await communityAPI.createPost({ title, content, category });
      toast.success('Post created! +20 XP');
      onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create post'); }
    finally { setCreating(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }}
        className="w-full max-w-lg rounded-xl border p-6 space-y-4"
        style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
        data-testid="create-post-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>New Post</h3>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--t-text-muted)' }} /></button>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--t-text-muted)' }}>Category</label>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(CATEGORY_ICONS).map(([id, CIcon]) => (
              <button key={id} onClick={() => setCategory(id)}
                className={`text-[10px] px-2.5 py-1 rounded-full border font-bold transition-colors flex items-center gap-1 ${
                  category === id ? 'bg-[rgba(13,148,136,0.15)] text-[#0D9488] border-[rgba(13,148,136,0.3)]' : ''
                }`}
                style={category !== id ? { borderColor: 'var(--t-border)', color: 'var(--t-text-muted)', background: 'var(--t-bg-card)' } : {}}>
                <CIcon className="w-3 h-3" />{id}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--t-text-muted)' }}>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
            placeholder="What's on your mind?"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:border-[#0D9488] focus:outline-none"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            data-testid="post-title-input" />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--t-text-muted)' }}>Content</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} maxLength={5000}
            placeholder="Share your thoughts, tips, or questions..."
            rows={5}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:border-[#0D9488] focus:outline-none"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            data-testid="post-content-input" />
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose} className="h-9 text-xs font-bold"
            style={{ background: 'var(--t-bg-card)', color: 'var(--t-text-secondary)', border: '1px solid var(--t-border)' }}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating || title.length < 5 || content.length < 10}
            className="h-9 text-xs font-bold uppercase tracking-wider"
            style={{ background: '#0D9488', color: '#fff' }}
            data-testid="submit-post-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Post
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function CommunityPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, sort };
      if (filter !== 'all') params.category = filter;
      const res = await communityAPI.posts(params);
      setPosts(res.data.posts);
      setTotalPages(res.data.pages);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, [page, sort, filter]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await communityAPI.categories();
      setCategories(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openPost = async (postId) => {
    try {
      const res = await communityAPI.post(postId);
      setSelectedPost(res.data);
    } catch { toast.error('Failed to load post'); }
  };

  const refreshPost = async () => {
    if (selectedPost) {
      const res = await communityAPI.post(selectedPost.post_id);
      setSelectedPost(res.data);
    }
  };

  if (selectedPost) {
    return (
      <div className="space-y-6 pb-12" data-testid="community-page">
        <PostDetail post={selectedPost} onBack={() => { setSelectedPost(null); fetchPosts(); }}
          onRefresh={refreshPost} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12" data-testid="community-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase mb-1" style={{ color: '#0D9488' }}>Community</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-none" style={{ color: 'var(--t-text-heading)' }}>
            Hub
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--t-text-muted)' }}>
            Connect, share, and learn from fellow career seekers
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}
          className="h-9 px-4 text-xs font-bold uppercase tracking-wider rounded-lg"
          style={{ background: '#0D9488', color: '#fff' }}
          data-testid="new-post-btn">
          <Plus className="w-4 h-4 mr-1" /> New Post
        </Button>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setFilter('all'); setPage(1); }}
          className={`text-xs px-3 py-1.5 rounded-full border font-bold transition-colors ${
            filter === 'all' ? 'bg-[rgba(13,148,136,0.15)] text-[#0D9488] border-[rgba(13,148,136,0.3)]' : ''
          }`}
          style={filter !== 'all' ? { borderColor: 'var(--t-border)', color: 'var(--t-text-muted)', background: 'var(--t-bg-section)' } : {}}
          data-testid="filter-all">
          All
        </button>
        {categories.map(cat => {
          const CIcon = CATEGORY_ICONS[cat.id] || MessageCircle;
          return (
            <button key={cat.id} onClick={() => { setFilter(cat.id); setPage(1); }}
              className={`text-xs px-3 py-1.5 rounded-full border font-bold transition-colors flex items-center gap-1 ${
                filter === cat.id ? 'bg-[rgba(13,148,136,0.15)] text-[#0D9488] border-[rgba(13,148,136,0.3)]' : ''
              }`}
              style={filter !== cat.id ? { borderColor: 'var(--t-border)', color: 'var(--t-text-muted)', background: 'var(--t-bg-section)' } : {}}
              data-testid={`filter-${cat.id}`}>
              <CIcon className="w-3 h-3" /> {cat.name} <span className="opacity-60">({cat.post_count})</span>
            </button>
          );
        })}
      </div>

      {/* Sort */}
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)' }} />
        {['newest', 'popular', 'most_commented'].map(s => (
          <button key={s} onClick={() => { setSort(s); setPage(1); }}
            className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider transition-colors ${
              sort === s ? 'text-[#0D9488]' : ''
            }`}
            style={sort !== s ? { color: 'var(--t-text-muted)' } : {}}
            data-testid={`sort-${s}`}>
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D9488' }} />
        </div>
      ) : posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl border"
          style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
          data-testid="empty-posts">
          <MessageCircle className="w-12 h-12 mb-3" style={{ color: 'var(--t-text-muted)', opacity: 0.4 }} />
          <p className="text-sm font-medium" style={{ color: 'var(--t-text-heading)' }}>No posts yet</p>
          <p className="text-xs mt-1 mb-4" style={{ color: 'var(--t-text-muted)' }}>Be the first to start a discussion!</p>
          <Button onClick={() => setShowCreate(true)} className="h-8 text-xs font-bold" style={{ background: '#0D9488', color: '#fff' }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Create Post
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(p => <PostCard key={p.post_id} post={p} onClick={() => openPost(p.post_id)} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={`w-8 h-8 rounded-lg text-xs font-bold ${page === i + 1 ? 'text-white' : ''}`}
              style={page === i + 1 ? { background: '#0D9488' } : { background: 'var(--t-bg-section)', color: 'var(--t-text-muted)' }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreate && <CreatePostModal onClose={() => setShowCreate(false)} onCreated={fetchPosts} />}
      </AnimatePresence>
    </div>
  );
}
